/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSetupDialog, uiTypes } from '@opengeoweb/store';
import DataExplorer from './DataExplorer';
import { DatasetActionsProvider } from './DatasetActionsContext';
import type { AddDatasetTimeseries } from './DatasetActionsContext';
import { dataExplorerActions, dataExplorerSelectors } from '../store';
import { DataExplorerInfoConnect } from './DataExplorerInfo/DataExplorerInfoConnect';
import type { DataExplorerModuleState } from '../store/types';
import {
  useFetchItems,
  useGetDataexplorerConfiguration,
} from '../utils/dataexplorer-api/hooks';

interface Props {
  productConfigKey: string;
  onAddTimeseries?: AddDatasetTimeseries;
}

export const DataExplorerConnect: React.FC<Props> = ({
  productConfigKey,
  onAddTimeseries,
}) => {
  const { dialogOrder, setDialogOrder, onCloseDialog, isDialogOpen, uiSource } =
    useSetupDialog(uiTypes.DialogTypes.DataExplorer);

  const { data: dataexplorerPresets } =
    useGetDataexplorerConfiguration(productConfigKey);

  const dispatch = useDispatch();

  const baseUrl = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getDiscoveryCatalogueURL(store),
  );

  const searchFilter = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getSearchFilter(store),
  );

  const queryables = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getQueryables(store),
  );

  const concepts = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getConcepts(store),
  );

  const bbox = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getBbox(store),
  );

  const meteogateOnly = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getMeteogateOnly(store),
  );

  const showMeteoGateBranding = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getShowMeteoGateBranding(store),
  );

  const { isFetching: loading, error } = useFetchItems(
    baseUrl,
    searchFilter,
    queryables,
    concepts,
    bbox,
    meteogateOnly,
    isDialogOpen,
  );

  React.useEffect(() => {
    if (dataexplorerPresets) {
      dispatch(
        dataExplorerActions.setDataExplorerConfiguration(dataexplorerPresets),
      );
    }
  }, [dispatch, dataexplorerPresets]);

  return React.useMemo(() => {
    return (
      <DatasetActionsProvider onAddTimeseries={onAddTimeseries}>
        <DataExplorer
          error={!!error}
          headerSize="xs"
          isOpen={isDialogOpen}
          loading={loading}
          onClose={onCloseDialog}
          onMouseDown={setDialogOrder}
          order={dialogOrder}
          showMeteoGateBranding={showMeteoGateBranding}
          source={uiSource}
          startPosition={{ top: 85, left: 50 }}
        />
        <DataExplorerInfoConnect />
      </DatasetActionsProvider>
    );
  }, [
    dialogOrder,
    isDialogOpen,
    onCloseDialog,
    setDialogOrder,
    uiSource,
    loading,
    error,
    showMeteoGateBranding,
    onAddTimeseries,
  ]);
};
